import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { scrapeRunsApi, type ScrapeRunDetail } from '../../api/scrapeRuns';

const RUN_TYPE_LABEL: Record<string, string> = {
  market_list: 'Marktliste',
  detail_monitor: 'Inserat-Überwachung',
  single_detail: 'Einzel-Detail',
  batch_detail: 'Detail-Batch',
};

const STATUS_LABEL: Record<string, string> = {
  running: 'Läuft',
  completed: 'Abgeschlossen',
  completed_with_errors: 'Mit Fehlern',
  failed: 'Fehlgeschlagen',
  skipped_locked: 'Übersprungen (gesperrt)',
};

const LEVEL_LABEL: Record<string, string> = {
  error: 'Fehler',
  warning: 'Warnung',
  info: 'Hinweis',
};

const STATUS_CLASS: Record<string, string> = {
  running: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  completed_with_errors: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-800',
  skipped_locked: 'bg-gray-100 text-gray-600',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CLASS[status] || 'bg-gray-100 text-gray-700'}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function fmt(dt: string | null): string {
  return dt ? new Date(dt).toLocaleString('de-DE') : '–';
}

function RunDetailRows({ runId }: { runId: number }) {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery<ScrapeRunDetail>({
    queryKey: ['scrape-run', runId],
    queryFn: () => scrapeRunsApi.get(runId),
  });

  if (isLoading) return <div className="px-4 py-3 text-sm text-gray-500">Lädt…</div>;
  if (!data) return null;

  return (
    <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 space-y-2">
      {data.error && (
        <div className="text-sm text-red-700">
          <span className="font-medium">Lauf-Fehler:</span> {data.error}
        </div>
      )}
      {data.entries.length === 0 && !data.error ? (
        <div className="text-sm text-gray-500">Keine Fehler in diesem Lauf.</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1 pr-4 font-medium">Zeit</th>
              <th className="py-1 pr-4 font-medium">Stufe</th>
              <th className="py-1 pr-4 font-medium">Inserat</th>
              <th className="py-1 font-medium">Meldung</th>
            </tr>
          </thead>
          <tbody>
            {data.entries.map((e) => (
              <tr key={e.id} className="border-t border-gray-200 align-top">
                <td className="py-1 pr-4 whitespace-nowrap text-gray-500">{fmt(e.created_at)}</td>
                <td className="py-1 pr-4">
                  <span className={`text-xs font-medium ${e.level === 'error' ? 'text-red-700' : e.level === 'warning' ? 'text-amber-700' : 'text-gray-600'}`}>
                    {LEVEL_LABEL[e.level] || e.level}
                  </span>
                </td>
                <td className="py-1 pr-4 whitespace-nowrap">
                  {e.listing_id ? (
                    <button
                      onClick={() => navigate(`/inserat/${e.listing_id}`)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {e.listing_title || `#${e.listing_id}`} &rarr;
                    </button>
                  ) : (
                    <span className="text-gray-400">–</span>
                  )}
                </td>
                <td className="py-1 text-gray-800 break-all">{e.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function ScrapeLog() {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [expanded, setExpanded] = useState<number | null>(null);

  const { data: runs, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['scrape-runs', statusFilter],
    queryFn: () => scrapeRunsApi.list({ status: statusFilter || undefined, limit: 100 }),
  });

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-semibold">Abruf-Protokoll</h1>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="px-3 py-1.5 border border-gray-300 rounded text-sm disabled:opacity-50"
        >
          {isFetching ? 'Aktualisiere…' : 'Aktualisieren'}
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Läufe der Markt- und Inserat-Überwachung inkl. aufgetretener Fehler (welcher Lauf, welches Inserat, welche Meldung).
      </p>

      <div className="mb-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded text-sm"
        >
          <option value="">Alle Status</option>
          <option value="completed">Abgeschlossen</option>
          <option value="completed_with_errors">Mit Fehlern</option>
          <option value="failed">Fehlgeschlagen</option>
          <option value="skipped_locked">Übersprungen (Lock)</option>
          <option value="running">Läuft</option>
        </select>
      </div>

      {isLoading ? (
        <div className="text-gray-500">Lädt…</div>
      ) : !runs || runs.length === 0 ? (
        <div className="text-gray-500">Noch keine Abrufe protokolliert.</div>
      ) : (
        <div className="bg-white rounded shadow border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2 font-medium">Start</th>
                <th className="px-4 py-2 font-medium">Typ</th>
                <th className="px-4 py-2 font-medium">Stadt</th>
                <th className="px-4 py-2 font-medium">Auslöser</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium text-right">Geprüft</th>
                <th className="px-4 py-2 font-medium text-right">Online</th>
                <th className="px-4 py-2 font-medium text-right">Offline</th>
                <th className="px-4 py-2 font-medium text-right">Fehler</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <Fragment key={r.id}>
                  <tr
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                    className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-2 whitespace-nowrap">{fmt(r.started_at)}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{RUN_TYPE_LABEL[r.run_type] || r.run_type}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{r.city_name || (r.city_id ? `#${r.city_id}` : '–')}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{r.trigger === 'scheduler' ? 'Zeitplan' : 'Manuell'}</td>
                    <td className="px-4 py-2 whitespace-nowrap"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-2 text-right">{r.checked}</td>
                    <td className="px-4 py-2 text-right">{r.online}</td>
                    <td className="px-4 py-2 text-right">{r.offline}</td>
                    <td className={`px-4 py-2 text-right font-medium ${r.error_count > 0 || r.failed > 0 ? 'text-red-700' : 'text-gray-400'}`}>
                      {r.error_count || r.failed || 0}
                    </td>
                  </tr>
                  {expanded === r.id && (
                    <tr>
                      <td colSpan={9} className="p-0">
                        <RunDetailRows runId={r.id} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
